import type { FocusEvent, ReactNode, RefObject } from "react";
import { useEffect, useId, useState, startTransition } from "react";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/molecules/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
  useCommandState,
} from "@/components/molecules/command";
import { DropdownPanelCommandItem } from "@/components/molecules/dropdownPanel/dropdownPanelCommandItem";
import { cn } from "@/lib/utils";

export interface DirectiveOptionFieldSuggestedValuesOption {
  value: string;
  label: string;
}

export interface DirectiveOptionFieldSuggestedValuesProps {
  suggestedOptions: DirectiveOptionFieldSuggestedValuesOption[];
  value: string;
  onChange: (value: string) => void;
  label?: ReactNode;
  id?: string;
  placeholder?: string;
  inputRef?: RefObject<HTMLInputElement | null>;
  /** @default true */
  autoFocus?: boolean;
}

type SuggestedValuesCommandInputProps = {
  inputRef?: RefObject<HTMLInputElement | null>;
  inputValue: string;
  onInputValueChange: (value: string) => void;
  onCommitFreeText: (trimmed: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
};

const SuggestedValuesCommandInput = ({
  inputRef,
  inputValue,
  onInputValueChange,
  onCommitFreeText,
  placeholder,
  autoFocus,
  onFocus,
}: SuggestedValuesCommandInputProps) => {
  const selectedItemId = useCommandState((s) => s.selectedItemId);

  return (
    <CommandInput
      ref={inputRef}
      value={inputValue}
      onValueChange={onInputValueChange}
      hideSearchIcon
      omitWrapperBottomBorder
      wrapperClassName="flex-1 min-w-0 border-0 p-0 h-full items-center"
      placeholder={placeholder}
      autoFocus={autoFocus}
      className={cn(
        `
          h-9 border-0 bg-transparent py-1 text-base shadow-none
          placeholder:text-foreground/70
          focus-visible:ring-0 focus-visible:outline-none
          disabled:cursor-not-allowed disabled:opacity-50
          md:text-sm
        `,
      )}
      onKeyDown={(e) => {
        if (e.key !== "Enter") {
          return;
        }
        if (selectedItemId != null && selectedItemId !== "") {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        onCommitFreeText(inputValue.trim());
      }}
      onFocus={onFocus}
    />
  );
};

/**
 * Suggested-values field for directive option slots: input with optional suggestions.
 * User may pick a suggestion or type any value. Submit on Enter or when a suggestion is selected.
 */
export const DirectiveOptionFieldSuggestedValues = ({
  suggestedOptions,
  value,
  onChange,
  label,
  id,
  placeholder = "Type or pick…",
  inputRef,
  autoFocus = true,
}: DirectiveOptionFieldSuggestedValuesProps): ReactNode => {
  const [inputValue, setInputValue] = useState(value);
  const [open, setOpen] = useState(false);
  const autoLabelId = useId();

  useEffect(() => {
    startTransition(() => {
      setInputValue(value);
    });
  }, [value]);

  const commit = (v: string) => {
    const trimmed = v.trim();
    if (trimmed.length > 0) {
      onChange(trimmed);
    }
    setOpen(false);
  };

  const commandLabel = placeholder ?? "Suggested value";
  const labelElementId =
    label != null ? (id != null ? `${id}-label` : autoLabelId) : undefined;

  return (
    <div
      className="space-y-2"
      role={label != null ? "group" : undefined}
      aria-labelledby={labelElementId}
    >
      {label != null && (
        <div className="text-sm text-foreground/45" id={labelElementId}>
          {label}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <Command
          shouldFilter
          label={commandLabel}
          className="overflow-visible bg-transparent"
        >
          <PopoverAnchor asChild>
            <div
              className={cn(
                `
                  flex h-9 w-full rounded-md border
                  border-(--console-border-input) bg-transparent px-3
                  transition-colors
                  focus-within:ring-1 focus-within:ring-ring
                  focus-within:outline-none
                `,
              )}
            >
              <SuggestedValuesCommandInput
                inputRef={inputRef}
                inputValue={inputValue}
                onInputValueChange={setInputValue}
                onCommitFreeText={(trimmed) => {
                  commit(trimmed);
                }}
                placeholder={placeholder}
                autoFocus={autoFocus}
                onFocus={() => {
                  setOpen(true);
                }}
              />
            </div>
          </PopoverAnchor>
          <PopoverContent
            dropdownPanel
            dropdownPanelScrollable
            align="start"
            side="bottom"
            sideOffset={2}
            className="w-radix-popover-trigger min-w-32"
            onOpenAutoFocus={(e) => {
              e.preventDefault();
            }}
          >
            <CommandList className="max-h-48">
              <CommandEmpty className="text-sm text-foreground/45">
                No matching suggestions.
              </CommandEmpty>
              <CommandGroup>
                {suggestedOptions.map((opt) => (
                  <DropdownPanelCommandItem
                    key={opt.value}
                    value={`${opt.label} ${opt.value}`}
                    keywords={[opt.value, opt.label]}
                    onSelect={() => {
                      setInputValue(opt.value);
                      commit(opt.value);
                    }}
                  >
                    {opt.label}
                  </DropdownPanelCommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </PopoverContent>
        </Command>
      </Popover>
    </div>
  );
};
